/**
 * trace-dialog.tsx — Hộp thoại "số này ở đâu ra".
 *
 * Mở khi bấm vào bất kỳ con số nào trên màn Tổng quan. Ba phần, theo đúng thứ
 * tự câu hỏi người xem đặt ra:
 *
 *  1. Con số là bao nhiêu, của kỳ nào.
 *  2. Lấy từ bảng nào, lọc những điều kiện gì (và công thức, nếu là tỉ lệ).
 *  3. Chính những dòng dữ liệu đã cộng thành con số đó.
 *
 * Backend dùng CHUNG một mệnh đề WHERE cho cả phép đếm lẫn phép lấy dòng, nên
 * tổng ở phần 1 và bảng ở phần 3 không thể lệch nhau.
 */
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { ChevronLeft, ChevronRight, Database, Sigma } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { useDrilldown, type ChiSoTruyVet, type CotTruyVet } from '@/hooks/use-drilldown'
import type { ChatReportMode, ChatReportScope } from '@/hooks/use-chat-report'
import { cn, formatNumber } from '@/lib/utils'

/** Chỉ số nào hiển thị dạng tiền / phần trăm thay vì số đếm. */
const TIEN: ChiSoTruyVet[] = ['revenue']
const PHAN_TRAM: ChiSoTruyVet[] = ['msgPerFriend', 'afterHoursPct', 'convRate', 'aiPct']

function dinhDangTong(metric: ChiSoTruyVet, v: number): string {
  if (TIEN.includes(metric)) return `${formatNumber(Math.round(v))} ₫`
  if (PHAN_TRAM.includes(metric)) {
    // Không kẹp trần 100%: tin nhắn / liên hệ vượt 100% là chuyện bình thường
    // khi một liên hệ nhắn nhiều lượt trong kỳ.
    return `${(v * 100).toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
  }
  return formatNumber(v)
}

function oO(v: unknown, cot: CotTruyVet): string {
  if (v == null || v === '') return '—'
  switch (cot.kieu) {
    case 'ngay':
      return dayjs(v as string).format('DD/MM/YYYY HH:mm')
    case 'tien':
      return `${formatNumber(Math.round(Number(v)))} ₫`
    case 'so':
      return formatNumber(Number(v))
    default:
      return String(v)
  }
}

export function TraceDialog({
  metric, mode, scope, onClose,
}: {
  metric: ChiSoTruyVet | null
  mode: ChatReportMode
  scope?: ChatReportScope
  onClose: () => void
}) {
  const [trang, setTrang] = useState(1)
  // Đổi chỉ số thì về trang 1 — giữ trang cũ của chỉ số khác là vô nghĩa.
  useEffect(() => { setTrang(1) }, [metric])

  const { data, isLoading, isError } = useDrilldown(metric, mode, scope, trang)

  const soTrang = data ? Math.max(1, Math.ceil(data.soDong / data.soDongMoiTrang)) : 1
  const dauTrang = data ? (data.trang - 1) * data.soDongMoiTrang : 0

  return (
    <Dialog open={!!metric} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="pr-8">{data?.tieuDe ?? 'Truy vết số liệu'}</DialogTitle>
          <DialogDescription>
            {data
              ? data.theoKy
                ? `Kỳ: ${data.kyNhan}`
                : 'Ảnh chụp hiện tại — không phụ thuộc kỳ lọc'
              : 'Đang lấy dữ liệu…'}
          </DialogDescription>
        </DialogHeader>

        {isError ? (
          <ErrorState message="Không truy vết được chỉ số này" />
        ) : isLoading || !data ? (
          <Loading />
        ) : (
          <div className="space-y-4">
            {/* ① Con số */}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border bg-muted/40 px-4 py-3">
              <span className="text-3xl font-bold tabular-nums">
                {dinhDangTong(data.metric, data.tong)}
              </span>
              <span className="text-xs text-muted-foreground">
                {data.congThuc
                  // Tỉ lệ: con số là thương, bảng bên dưới là các dòng của TỬ SỐ.
                  ? `tính từ ${formatNumber(data.soDong)} dòng tử số liệt kê bên dưới`
                  : data.cachTinhTong === 'cong'
                    ? `là tổng cột số, gom từ ${formatNumber(data.soDong)} dòng dữ liệu`
                    : `bằng đúng ${formatNumber(data.soDong)} dòng dữ liệu bên dưới`}
              </span>
            </div>

            {/* ② Nguồn + công thức */}
            <div className="space-y-2 text-xs">
              <p className="flex items-center gap-1.5 font-semibold">
                <Database className="h-3.5 w-3.5 text-muted-foreground" />
                Nguồn dữ liệu
              </p>
              <p className="font-mono text-[11px] text-muted-foreground">{data.nguon.bang}</p>
              <ul className="ml-4 list-disc space-y-1 text-muted-foreground">
                {data.nguon.dieuKien.map((d) => <li key={d}>{d}</li>)}
              </ul>

              {data.congThuc && (
                <div className="rounded-md border border-dashed p-3">
                  <p className="flex items-center gap-1.5 font-semibold">
                    <Sigma className="h-3.5 w-3.5 text-muted-foreground" />
                    Công thức: {data.congThuc.bieuThuc}
                  </p>
                  <div className="mt-1.5 space-y-0.5 text-muted-foreground">
                    {data.congThuc.thanhPhan.map((t) => (
                      <p key={t.nhan}>
                        {t.nhan}: <span className="font-semibold text-foreground">{formatNumber(Number(t.giaTri))}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {data.ghiChu && (
                <p className="rounded-md bg-warning/10 px-3 py-2 text-warning">{data.ghiChu}</p>
              )}
            </div>

            {/* ③ Dữ liệu chi tiết */}
            {data.dong.length === 0 ? (
              <p className="rounded-md border border-dashed py-8 text-center text-xs text-muted-foreground">
                Không có dòng dữ liệu nào trong phạm vi này.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                {data.dong.some((r) => r.hoi_thoai_id) && (
                  <p className="border-b bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground">Bấm vào một dòng để mở hội thoại đó ở tab mới.</p>
                )}
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/70 text-left">
                    <tr>
                      <th className="w-10 px-2 py-2 font-semibold text-muted-foreground">#</th>
                      {data.cot.map((c) => (
                        <th key={c.key} className="whitespace-nowrap px-3 py-2 font-semibold">{c.nhan}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.dong.map((r, i) => {
                      const convId = typeof r.hoi_thoai_id === 'string' ? r.hoi_thoai_id : null
                      // Mở hội thoại ở TAB MỚI: người xem báo cáo đang đối chiếu
                      // nhiều dòng, nhảy trang là mất bảng đang xem.
                      const mo = () => { if (convId) window.open(`/conversations/${convId}`, '_blank', 'noopener') }
                      return (
                      <tr
                        key={i}
                        className={cn('border-t align-top hover:bg-accent/40', convId && 'cursor-pointer')}
                        onClick={mo}
                        onKeyDown={(e) => { if (convId && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); mo() } }}
                        tabIndex={convId ? 0 : undefined}
                        role={convId ? 'link' : undefined}
                        title={convId ? 'Mở hội thoại ở tab mới' : undefined}
                      >
                        <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                          {convId ? <ExternalLink className="inline h-3 w-3 text-primary" /> : dauTrang + i + 1}
                        </td>
                        {data.cot.map((c) => (
                          <td
                            key={c.key}
                            className={cn(
                              'px-3 py-1.5',
                              c.kieu === 'so' || c.kieu === 'tien' ? 'tabular-nums' : '',
                              c.key === 'noi_dung' ? 'min-w-[16rem] max-w-md' : 'whitespace-nowrap',
                            )}
                          >
                            {oO(r[c.key], c)}
                          </td>
                        ))}
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {soTrang > 1 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Dòng {formatNumber(dauTrang + 1)}–{formatNumber(dauTrang + data.dong.length)} trên{' '}
                  {formatNumber(data.soDong)}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={trang <= 1} onClick={() => setTrang((t) => t - 1)}>
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Trước
                  </Button>
                  <span className="tabular-nums">{trang}/{formatNumber(soTrang)}</span>
                  <Button
                    variant="outline" size="sm"
                    disabled={trang >= soTrang}
                    onClick={() => setTrang((t) => t + 1)}
                  >
                    Sau
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
